import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Globe, X } from "lucide-react";
import { api } from "@/lib/api";

// Region groups - now aligned with actual PIPECO branches.
const REGIONS = [
  { key: "middle-east", label: "Middle East", codes: ["AE", "SA", "QA", "OM", "BH", "KW", "JO"] },
  { key: "asia", label: "Asia", codes: ["MY", "IN", "SG", "ID", "TH", "PH", "VN", "CN", "JP", "LK"] },
  { key: "africa", label: "Africa", codes: ["ZA", "NG", "EG", "KE", "ZW", "ET", "GH"] },
  { key: "europe", label: "Europe", codes: ["GB", "DE", "FR", "IT", "ES", "NL"] },
  { key: "americas", label: "Americas", codes: ["US", "CA", "BR", "MX"] },
  { key: "oceania", label: "Oceania", codes: ["AU", "NZ"] },
];

function RegionMiniMap({ region }) {
  return (
    <span data-testid={`region-map-${region}`} className="block h-9 w-11 shrink-0 sm:h-11 sm:w-14" aria-hidden="true">
      <img
        src={`/region-icons/${region}.png`}
        alt=""
        className="h-full w-full object-contain"
        draggable="false"
      />
    </span>
  );
}

export default function RegionPopup() {
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [region, setRegion] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const seen = sessionStorage.getItem("pipeco_region_seen");
    // Show on first visit each browsing session (more reliable than one-time-ever localStorage).
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (open) api.get("/branches").then(r => setBranches(r.data)).catch(()=>{});
  }, [open]);

  const dismiss = () => { sessionStorage.setItem("pipeco_region_seen", "1"); setOpen(false); };

  const visitGlobalWebsite = () => {
    sessionStorage.setItem("pipeco_region_seen", "1");
    setOpen(false);
    navigate("/");
  };

  const goToCountry = (country) => {
    sessionStorage.setItem("pipeco_region_seen", "1");
    localStorage.setItem("pipeco_country", country);
    setOpen(false);
    navigate(`/contact?country=${encodeURIComponent(country)}`);
  };

  if (!open) return null;

  const regionCountries = region
    ? branches.filter(b => REGIONS.find(r=>r.key===region)?.codes.includes(b.country_code))
    : [];

  return (
    <div data-testid="region-popup" className="fixed inset-0 z-[60] grid place-items-center overflow-y-auto bg-black/65 px-3 py-4 backdrop-blur-sm fade-in-up sm:px-5 sm:py-6" role="dialog" aria-modal="true" aria-labelledby="region-popup-title">
      <div data-testid="region-popup-panel" className="relative w-full max-w-4xl max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)]">
        <button
          data-testid="region-popup-close"
          onClick={dismiss}
          className="region-popup-action absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center border border-slate-200 bg-white text-slate-700 transition-[background-color,color,border-color,transform] duration-200 active:scale-95 sm:right-5 sm:top-5"
          aria-label="Close region selection"
        >
          <X size={20}/>
        </button>
        <div className="p-4 sm:p-8 lg:p-10">
          <div className="mb-3 flex items-center gap-3 pr-12 sm:mb-4 sm:gap-4">
            <img data-testid="region-popup-logo" src="/pipeco-logo.png" alt="PIPECO GROUP" className="h-7 w-auto sm:h-9"/>
            <span data-testid="region-popup-eyebrow" className="overline text-pipeco-green">Choose your region</span>
          </div>
          <h2 data-testid="region-popup-title" className="text-slate-900">
            <span data-testid="region-popup-title-prefix" className="overline block text-[9px] text-pipeco-green sm:text-xs">Welcome to</span>
            <span data-testid="region-popup-title-brand" className="headline mt-1 block text-2xl sm:text-4xl lg:text-5xl">PIPECO GROUP</span>
          </h2>
          <p data-testid="region-popup-description" className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 sm:mt-3 sm:text-base">Visit our global website or select your region for local support.</p>

          {!region ? (
            <div className="mt-5 sm:mt-8">
              <button
                data-testid="region-global-website"
                onClick={visitGlobalWebsite}
                className="region-popup-primary group flex w-full items-center justify-between gap-3 border border-pipeco-green bg-pipeco-green p-3 text-left text-white transition-[background-color,border-color,transform,box-shadow] duration-200 hover:shadow-lg active:scale-[0.99] sm:gap-4 sm:p-4"
              >
                <span className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <span className="grid h-9 w-9 shrink-0 place-items-center border border-white/30 bg-white/10 sm:h-10 sm:w-10" aria-hidden="true">
                    <Globe size={20}/>
                  </span>
                  <span className="min-w-0">
                    <span data-testid="region-global-title" className="block font-display text-base font-bold uppercase leading-tight sm:text-lg">Our Global Website</span>
                    <span data-testid="region-global-subtitle" className="mt-0.5 block text-[10px] leading-4 text-white/80 sm:text-xs">Continue to our global corporate website</span>
                  </span>
                </span>
                <ArrowRight className="shrink-0 transition-transform duration-200 group-hover:translate-x-1" size={20}/>
              </button>

              <div data-testid="region-selection-divider" className="my-4 flex items-center gap-3 sm:my-6">
                <span className="h-px flex-1 bg-slate-200" aria-hidden="true"/>
                <span className="overline whitespace-nowrap text-slate-500">Or select your region</span>
                <span className="h-px flex-1 bg-slate-200" aria-hidden="true"/>
              </div>

              <div data-testid="region-card-grid" className="grid auto-rows-fr grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
                {REGIONS.map(r => (
                  <button key={r.key} data-testid={`region-${r.key}`} onClick={()=>setRegion(r.key)}
                    className="region-popup-action flex min-h-[76px] min-w-0 items-center gap-1.5 border border-slate-200 bg-white p-2 text-left text-slate-900 transition-[background-color,color,border-color,transform,box-shadow] duration-200 hover:shadow-sm active:scale-[0.98] sm:min-h-[86px] sm:gap-3 sm:p-3">
                    <RegionMiniMap region={r.key}/>
                    <span className="block min-w-0 flex-1">
                      <span className="block whitespace-nowrap font-display text-[13px] font-bold uppercase leading-tight sm:text-lg">{r.label}</span>
                      <span className="mt-0.5 block whitespace-nowrap text-[7px] font-semibold uppercase leading-tight text-slate-500 sm:text-[9px] sm:tracking-wider">Select region</span>
                    </span>
                    <ArrowRight className="shrink-0 text-pipeco-green" size={14} aria-hidden="true"/>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <div data-testid="region-country-heading" className="text-sm text-slate-600">Countries in <b>{REGIONS.find(r=>r.key===region)?.label}</b></div>
                <button data-testid="region-back-button" onClick={()=>setRegion(null)} className="region-popup-action border border-transparent px-3 py-2 text-xs uppercase tracking-widest text-pipeco-green transition-[background-color,color,border-color,transform] duration-200 active:scale-95">← Back</button>
              </div>
              {regionCountries.length === 0 ? (
                <div data-testid="region-no-office-message" className="border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                  No PIPECO office listed for this region yet.
                  <button data-testid="region-contact-anyway" onClick={()=>goToCountry("")} className="region-popup-action ml-2 border border-transparent px-2 py-1 font-semibold text-pipeco-green underline transition-[background-color,color,border-color] duration-200">Contact us anyway →</button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {regionCountries.map(b => (
                    <button key={b.id} data-testid={`region-country-${b.country_code}`} onClick={()=>goToCountry(b.country)}
                      className="region-popup-action border border-slate-200 bg-white p-4 text-left text-slate-900 transition-[background-color,color,border-color,transform,box-shadow] duration-200 hover:shadow-md active:scale-[0.98]">
                      <div className="font-semibold">{b.country}</div>
                      <div className="text-[11px] uppercase tracking-widest opacity-70">{b.city} · {b.branch_type}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-3 text-xs text-slate-500 sm:mt-6 sm:pt-4">
            <button onClick={dismiss} className="region-popup-action inline-flex min-h-10 shrink-0 items-center gap-2 whitespace-nowrap border border-transparent px-1 py-2 font-semibold uppercase tracking-wide text-pipeco-green transition-[background-color,color,border-color,transform] duration-200 active:scale-[0.98]" data-testid="region-skip">Skip &amp; Continue <ArrowRight size={15} aria-hidden="true"/></button>
            <span data-testid="region-popup-tagline" className="max-w-[44%] text-right text-[8px] uppercase leading-4 tracking-widest text-slate-500 sm:max-w-none sm:text-xs">Global Water Storage Solutions</span>
          </div>
        </div>
      </div>
    </div>
  );
}
